import { isoDate as melDate, melNowHHMM, todayISO as melToday } from "@/lib/intl";
import { useRollingToday } from "@/lib/useRollingToday";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus, RefreshCw, Trash2, Pencil, CheckCircle2, AlertTriangle, Search, X,
  Star, StarOff,
} from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/intl";
import Receipts from "@/pages/Receipts";

const PAYMENT_METHODS = [
  { value: "HICAPS", label: "HICAPS", badge: "amber" },
  { value: "EFTPOS", label: "EFTPOS / Visa / MC (NAB)", badge: "sky" },
  { value: "AMEX", label: "AMEX (NAB terminal — settles separately)", badge: "indigo" },
  { value: "STRIPE", label: "Stripe (online)", badge: "violet" },
  { value: "CASH", label: "Cash", badge: "emerald" },
  { value: "BANK_TRANSFER", label: "Bank transfer", badge: "zinc" },
  { value: "MEDICARE", label: "Medicare", badge: "rose" },
  { value: "OTHER", label: "Other", badge: "zinc" },
];

const METHOD_BADGE_CLASS = {
  HICAPS: "bg-amber-100 text-amber-900 border-amber-200",
  EFTPOS: "bg-sky-100 text-sky-900 border-sky-200",
  AMEX: "bg-indigo-100 text-indigo-900 border-indigo-200",
  STRIPE: "bg-violet-100 text-violet-900 border-violet-200",
  CASH: "bg-emerald-100 text-emerald-900 border-emerald-200",
  BANK_TRANSFER: "bg-zinc-100 text-zinc-800 border-zinc-200",
  MEDICARE: "bg-rose-100 text-rose-900 border-rose-200",
  OTHER: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function todayISO() {
  return melToday();
}

function nowHHMM() {
  return melNowHHMM();
}

// ---------------------------------------------------------------------------
// Providers tab — new schema (name + provider_id + initials, no terminal ID)
// ---------------------------------------------------------------------------
function ProvidersTab({ providers, refresh }) {
  const [draft, setDraft] = useState({
    name: "", provider_id: "", initials: "", sort_order: 0, is_default: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!draft.name.trim()) { toast.error("Provider name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        provider_id: draft.provider_id?.trim() || null,
        initials: draft.initials?.trim().toUpperCase() || null,
        active: true,
        sort_order: Number(draft.sort_order) || 0,
        is_default: !!draft.is_default,
      };
      if (editingId) {
        await api.put(`/reconciliation/providers/${editingId}`, body);
        toast.success("Provider updated");
      } else {
        await api.post("/reconciliation/providers", body);
        toast.success("Provider added");
      }
      setDraft({ name: "", provider_id: "", initials: "", sort_order: 0, is_default: false });
      setEditingId(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save provider");
    } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Remove this provider? Historical receipts keep the name for reports.")) return;
    try {
      await api.delete(`/reconciliation/providers/${id}`);
      toast.success("Provider removed");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  }

  async function makeDefault(id) {
    try {
      await api.post(`/reconciliation/providers/${id}/make-default`);
      toast.success("Default provider set");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set default");
    }
  }

  function startEdit(p) {
    setEditingId(p.id);
    setDraft({
      name: p.name || "",
      provider_id: p.provider_id || "",
      initials: p.initials || "",
      sort_order: p.sort_order || 0,
      is_default: !!p.is_default,
    });
  }

  return (
    <div className="space-y-4" data-testid="providers-tab">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3">
          {editingId ? "Edit provider" : "Add provider"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Full name *</Label>
            <Input value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Dr Rafael Marchese"
              data-testid="provider-name-input" />
          </div>
          <div>
            <Label className="text-xs">Provider ID</Label>
            <Input value={draft.provider_id}
              onChange={(e) => setDraft({ ...draft, provider_id: e.target.value })}
              placeholder="Medicare/HICAPS #"
              data-testid="provider-id-input" />
          </div>
          <div>
            <Label className="text-xs">Initials</Label>
            <Input value={draft.initials}
              onChange={(e) => setDraft({ ...draft, initials: e.target.value.toUpperCase() })}
              placeholder="RM" maxLength={4}
              data-testid="provider-initials-input" />
          </div>
          <label className="flex items-end gap-2 pb-2 cursor-pointer select-none"
            data-testid="provider-default-toggle">
            <input type="checkbox" checked={!!draft.is_default}
              onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
              className="h-4 w-4" />
            <span className="text-xs">Default for new receipts</span>
          </label>
          <div className="flex items-end gap-2">
            <Button onClick={save} disabled={saving} data-testid="provider-save-btn">
              {editingId ? "Update" : "Add"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={() => {
                setEditingId(null);
                setDraft({ name: "", provider_id: "", initials: "", sort_order: 0, is_default: false });
              }}>Cancel</Button>
            )}
          </div>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Initials</TableHead>
              <TableHead className="w-40">Provider ID</TableHead>
              <TableHead className="w-48 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {providers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-6">
                  No providers yet. Add your first practitioner above.
                </TableCell>
              </TableRow>
            ) : providers.map((p) => (
              <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
                <TableCell>
                  {p.is_default ? (
                    <Star className="h-4 w-4 fill-amber-400 text-amber-500"
                      aria-label="Default provider"
                      data-testid={`provider-default-star-${p.id}`} />
                  ) : (
                    <StarOff className="h-4 w-4 text-zinc-300" />
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="font-mono text-xs">{p.initials || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.provider_id || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  {!p.is_default && (
                    <Button size="sm" variant="outline" onClick={() => makeDefault(p.id)}
                      data-testid={`provider-make-default-${p.id}`}>
                      Set default
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)}
                    data-testid={`provider-edit-${p.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(p.id)}
                    data-testid={`provider-delete-${p.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Receipt dialog — multi-item + multi-split + Acuity product picker
// ---------------------------------------------------------------------------
function ReceiptDialog({ open, onOpenChange, providers, catalog, editingReceipt, onSaved }) {
  const [draft, setDraft] = useState(null);
  // Clients on the schedule for the receipt's date — used to power the
  // client-name autocomplete. Refreshed whenever the date changes.
  const [dayClients, setDayClients] = useState([]);
  const clientDatalistId = "receipt-client-suggestions";

  useEffect(() => {
    if (!open) return;
    if (editingReceipt) {
      setDraft({
        date: editingReceipt.date,
        time: editingReceipt.time || "",
        receipt_number: editingReceipt.receipt_number || "",
        client_name: editingReceipt.client_name || "",
        provider_id: editingReceipt.provider_pk || "",
        items: editingReceipt.items?.length
          ? editingReceipt.items.map((it) => ({
              description: it.description || "",
              amount: it.amount?.toString() || "",
              quantity: it.quantity || 1,
              source: it.source || "manual",
              acuity_ref: it.acuity_ref || null,
              category: "",
              certificate_code: it.certificate_code || "",
            }))
          : [{ description: "", amount: "", quantity: 1, source: "manual", acuity_ref: null, category: "", certificate_code: "" }],
        splits: editingReceipt.splits?.length
          ? editingReceipt.splits.map((sp) => ({ method: sp.method, amount: sp.amount?.toString() || "" }))
          : [{ method: "EFTPOS", amount: "" }],
        notes: editingReceipt.notes || "",
      });
    } else {
      // Pre-select the provider flagged is_default; fall back to first row.
      const defaultProvider = providers.find((p) => p.is_default) || providers[0];
      setDraft({
        date: todayISO(),
        time: nowHHMM(),
        receipt_number: "",
        client_name: "",
        provider_id: defaultProvider?.id || "",
        items: [{ description: "", amount: "", quantity: 1, source: "manual", acuity_ref: null, category: "", certificate_code: "" }],
        splits: [{ method: "EFTPOS", amount: "" }],
        notes: "",
      });
    }
  }, [open, editingReceipt, providers]);

  // Pre-fetch the next receipt number for creates
  useEffect(() => {
    if (!open || editingReceipt || !draft) return;
    let cancelled = false;
    api.get("/reconciliation/next-receipt-number", { params: { date: draft.date } })
      .then((r) => { if (!cancelled) setDraft((d) => d ? { ...d, receipt_number: r.data.receipt_number } : d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, editingReceipt, draft?.date]);

  // Load appointments on the receipt's date so we can autocomplete the
  // client-name field. Runs whenever the date changes; failures are
  // silent because manual typing still works.
  useEffect(() => {
    if (!open || !draft?.date) { setDayClients([]); return; }
    let cancelled = false;
    api.get("/appointments", { params: {
      minDate: draft.date, maxDate: draft.date, max: 500,
      enrich_price: false, enrich_intake: false,
    } })
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        // Dedup by full name (case-insensitive) — same client may have
        // multiple bookings that day.
        const seen = new Map();
        for (const a of rows) {
          const first = (a.firstName || "").trim();
          const last = (a.lastName || "").trim();
          const name = `${first} ${last}`.trim();
          if (!name) continue;
          const k = name.toLowerCase();
          if (seen.has(k)) continue;
          seen.set(k, {
            name,
            email: (a.email || "").trim(),
            type: (a.type || "").trim(),
            time: (a.datetime || "").slice(11, 16),
          });
        }
        setDayClients(Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => { if (!cancelled) setDayClients([]); });
    return () => { cancelled = true; };
  }, [open, draft?.date]);

  const itemsTotal = useMemo(() => {
    if (!draft) return 0;
    return draft.items.reduce(
      (s, it) => s + (parseFloat(it.amount) || 0) * (parseInt(it.quantity, 10) || 1),
      0,
    );
  }, [draft]);

  const splitsTotal = useMemo(() => {
    if (!draft) return 0;
    return draft.splits.reduce((s, sp) => s + (parseFloat(sp.amount) || 0), 0);
  }, [draft]);

  const splitDelta = useMemo(() => Math.round((splitsTotal - itemsTotal) * 100) / 100, [itemsTotal, splitsTotal]);

  // Sorted unique category list for the line-item filter. "All" always
  // included; ordering is Products first, then alphabetical categories.
  const catalogCategories = useMemo(() => {
    const set = new Set();
    catalog.forEach((c) => { if (c.category) set.add(c.category); });
    const arr = Array.from(set);
    const products = arr.includes("Products") ? ["Products"] : [];
    const rest = arr.filter((c) => c !== "Products").sort();
    return [...products, ...rest];
  }, [catalog]);

  if (!draft) return null;

  function updateItem(i, patch) {
    const items = draft.items.slice();
    items[i] = { ...items[i], ...patch };
    setDraft({ ...draft, items });
  }
  function addItem() {
    setDraft({ ...draft, items: [...draft.items, { description: "", amount: "", quantity: 1, source: "manual", acuity_ref: null, category: "" }] });
  }
  function removeItem(i) {
    if (draft.items.length <= 1) return;
    setDraft({ ...draft, items: draft.items.filter((_, idx) => idx !== i) });
  }
  function pickCatalogItem(i, catalogValue) {
    if (!catalogValue || catalogValue === "__manual__") {
      updateItem(i, { source: "manual", acuity_ref: null });
      return;
    }
    const c = catalog.find((c) => c.id === catalogValue);
    if (!c) return;
    updateItem(i, {
      description: c.name,
      amount: (c.price || 0).toString(),
      source: c.kind === "product" ? "acuity_product" : "acuity_type",
      acuity_ref: c.id,
      category: c.category || "",
    });
  }
  function setItemCategory(i, category) {
    // Category is a client-side narrowing filter — clear the current
    // picked catalog ref if the category changes so a stale product
    // doesn't stay pre-filled after the operator narrows the list.
    const items = draft.items.slice();
    const currentRef = items[i]?.acuity_ref;
    const currentEntry = currentRef ? catalog.find((c) => c.id === currentRef) : null;
    if (currentEntry && category !== "__all__" && currentEntry.category !== category) {
      items[i] = { ...items[i], category, acuity_ref: null, source: "manual" };
    } else {
      items[i] = { ...items[i], category };
    }
    setDraft({ ...draft, items });
  }

  function updateSplit(i, patch) {
    const splits = draft.splits.slice();
    splits[i] = { ...splits[i], ...patch };
    setDraft({ ...draft, splits });
  }
  function addSplit() {
    setDraft({ ...draft, splits: [...draft.splits, { method: "EFTPOS", amount: "" }] });
  }
  function removeSplit(i) {
    if (draft.splits.length <= 1) return;
    setDraft({ ...draft, splits: draft.splits.filter((_, idx) => idx !== i) });
  }

  function balanceSplits() {
    // Set the LAST split amount = remaining unallocated total
    const others = draft.splits.slice(0, -1).reduce((s, sp) => s + (parseFloat(sp.amount) || 0), 0);
    const remaining = Math.max(0, Math.round((itemsTotal - others) * 100) / 100);
    const splits = draft.splits.slice();
    splits[splits.length - 1] = { ...splits[splits.length - 1], amount: remaining.toString() };
    setDraft({ ...draft, splits });
  }

  async function save() {
    // Validation
    for (const it of draft.items) {
      if (!it.description.trim()) { toast.error("Every line item needs a description"); return; }
      if (!it.amount || Number(it.amount) <= 0) { toast.error("Line item unit price must be > 0"); return; }
      const q = parseInt(it.quantity, 10);
      if (!q || q < 1) { toast.error("Line item quantity must be at least 1"); return; }
    }
    for (const sp of draft.splits) {
      if (!sp.method) { toast.error("Every split needs a payment method"); return; }
      if (!sp.amount || Number(sp.amount) <= 0) { toast.error("Split amounts must be > 0"); return; }
    }
    if (draft.splits.some((sp) => sp.method === "HICAPS") && !draft.provider_id) {
      toast.error("HICAPS splits require a provider"); return;
    }
    if (Math.abs(splitDelta) > 0.005) {
      const proceed = window.confirm(
        `Splits total ${fmtMoney(splitsTotal)} but items total ${fmtMoney(itemsTotal)} (off by ${fmtMoney(Math.abs(splitDelta))}). Save anyway?`
      );
      if (!proceed) return;
    }
    try {
      const body = {
        date: draft.date,
        time: draft.time || null,
        client_name: draft.client_name || null,
        provider_id: draft.provider_id || null,
        items: draft.items.map((it) => ({
          description: it.description.trim(),
          amount: Number(it.amount),
          quantity: parseInt(it.quantity, 10) || 1,
          source: it.source,
          acuity_ref: it.acuity_ref,
          certificate_code: (it.certificate_code || "").trim() || null,
        })),
        splits: draft.splits.map((sp) => ({ method: sp.method, amount: Number(sp.amount) })),
        notes: draft.notes || null,
      };
      if (editingReceipt) {
        body.receipt_number = draft.receipt_number;
        await api.put(`/reconciliation/receipts/${editingReceipt.id}`, body);
        toast.success("Receipt updated");
      } else {
        await api.post("/reconciliation/receipts", body);
        toast.success(`Receipt ${draft.receipt_number} saved`);
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save receipt");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingReceipt ? `Edit receipt ${editingReceipt.receipt_number}` : "New receipt"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Header row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Receipt #</Label>
              <Input value={draft.receipt_number} readOnly
                className="bg-zinc-50 font-mono text-sm" data-testid="receipt-number-input" />
            </div>
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                data-testid="receipt-date-input" />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={draft.time}
                onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                data-testid="receipt-time-input" />
            </div>
            <div>
              <Label className="text-xs">Provider</Label>
              <Select value={draft.provider_id || "__none__"}
                onValueChange={(v) => setDraft({ ...draft, provider_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="receipt-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.initials ? `${p.initials} · ` : ""}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">
              Client name
              {dayClients.length > 0 && (
                <span className="ml-2 text-2xs text-zinc-500">
                  · {dayClients.length} on today&apos;s schedule
                </span>
              )}
            </Label>
            <Input value={draft.client_name}
              list={clientDatalistId}
              onChange={(e) => {
                const name = e.target.value;
                // Auto-fill the first line item when the picked name matches
                // a scheduled appointment today. We look the appointment type
                // up in the catalog to pull in the price; if the type isn't
                // in the catalog we still populate the description so the
                // operator only needs to type the price.
                const match = dayClients.find((c) => c.name === name);
                if (!match || !match.type) {
                  setDraft({ ...draft, client_name: name });
                  return;
                }
                const first = draft.items[0];
                const firstIsEmpty =
                  !(first?.description || "").trim()
                  && !(first?.amount || "").toString().trim()
                  && !first?.acuity_ref;
                if (!firstIsEmpty) {
                  setDraft({ ...draft, client_name: name });
                  return;
                }
                // Case-insensitive match against the Acuity catalog so
                // "Onero – Bone Health" ≈ "Onero - Bone Health" still hits.
                const norm = (s) => (s || "").toLowerCase()
                  .replace(/[\u2010-\u2015\u2212]/g, "-")   // any dash → -
                  .replace(/\s+/g, " ")
                  .trim();
                const cat = catalog.find((c) => norm(c.name) === norm(match.type));
                const items = draft.items.slice();
                if (cat) {
                  items[0] = {
                    ...first,
                    description: cat.name,
                    amount: (cat.price || 0).toString(),
                    source: cat.kind === "product" ? "acuity_product" : "acuity_type",
                    acuity_ref: cat.id,
                    category: cat.category || "",
                    quantity: first?.quantity || 1,
                  };
                } else {
                  // Type not in the catalog — pre-fill the description only.
                  items[0] = {
                    ...first,
                    description: match.type,
                    source: "manual",
                    acuity_ref: null,
                    quantity: first?.quantity || 1,
                  };
                }
                setDraft({ ...draft, client_name: name, items });
              }}
              placeholder="Type or pick from today's appointments"
              autoComplete="off"
              data-testid="receipt-client-input" />
            <datalist id={clientDatalistId}>
              {dayClients.map((c) => (
                <option key={c.name} value={c.name}
                  label={[c.time, c.type].filter(Boolean).join(" · ")} />
              ))}
            </datalist>
          </div>

          {/* Line items */}
          <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Line items</span>
              <span className="text-xs text-zinc-500 font-mono-num">
                Total: {fmtMoney(itemsTotal)}
              </span>
            </div>
            {draft.items.map((it, i) => {
              const filteredCatalog = it.category && it.category !== "__all__"
                ? catalog.filter((c) => c.category === it.category)
                : catalog;
              const isAcuityProduct = it.source === "acuity_product";
              return (
              <div key={i} className="space-y-1" data-testid={`receipt-item-row-${i}`}>
              <div className="grid grid-cols-[140px_1fr_1fr_70px_110px_36px] items-end gap-2">
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">Category</Label>
                  <Select value={it.category || "__all__"}
                    onValueChange={(v) => setItemCategory(i, v === "__all__" ? "" : v)}>
                    <SelectTrigger className="h-9" data-testid={`item-category-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__all__">All categories</SelectItem>
                      {catalogCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">
                    Product / service
                    {it.category && it.category !== "__all__" && (
                      <span className="ml-1 text-2xs text-zinc-400">({filteredCatalog.length})</span>
                    )}
                  </Label>
                  <Select value={it.acuity_ref || "__manual__"} onValueChange={(v) => pickCatalogItem(i, v)}>
                    <SelectTrigger className="h-9" data-testid={`item-catalog-${i}`}>
                      <SelectValue placeholder="Pick from Acuity or type below" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__manual__">— Type manually —</SelectItem>
                      {filteredCatalog.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} · ${(c.price || 0).toFixed(2)}
                        </SelectItem>
                      ))}
                      {filteredCatalog.length === 0 && (
                        <div className="px-3 py-2 text-xs text-zinc-500">
                          No items in this category.
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">Description</Label>
                  <Input value={it.description}
                    onChange={(e) => updateItem(i, { description: e.target.value })}
                    placeholder="e.g. Onero1 session" className="h-9"
                    data-testid={`item-description-${i}`} />
                </div>
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">Qty</Label>
                  <Input type="number" min="1" step="1" value={it.quantity ?? 1}
                    onChange={(e) => updateItem(i, { quantity: e.target.value.replace(/[^0-9]/g, "") })}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      updateItem(i, { quantity: !v || v < 1 ? 1 : v });
                    }}
                    className="h-9 font-mono-num text-right"
                    data-testid={`item-quantity-${i}`} />
                </div>
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">
                    Unit price {it.source !== "manual" && <span className="text-amber-700">(override)</span>}
                  </Label>
                  <Input type="number" step="0.01" value={it.amount}
                    onChange={(e) => updateItem(i, { amount: e.target.value })}
                    className="h-9 font-mono-num"
                    data-testid={`item-amount-${i}`} />
                </div>
                <Button variant="outline" size="icon" className="h-9 w-9"
                  disabled={draft.items.length <= 1}
                  onClick={() => removeItem(i)}
                  data-testid={`item-remove-${i}`}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
              {(parseInt(it.quantity, 10) || 1) > 1 && parseFloat(it.amount) > 0 && (
                <div className="pl-[148px] text-2xs text-zinc-500 font-mono-num"
                  data-testid={`item-line-total-${i}`}>
                  Line total: {fmtMoney((parseInt(it.quantity, 10) || 1) * (parseFloat(it.amount) || 0))}
                </div>
              )}
              {isAcuityProduct && (
                <div className="pl-[148px]">
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">
                    Acuity certificate code
                    <span className="ml-2 text-2xs text-zinc-400 normal-case tracking-normal">
                      Paste the code Acuity issued when you created the manual Order
                    </span>
                  </Label>
                  <Input value={it.certificate_code || ""}
                    onChange={(e) => updateItem(i, { certificate_code: e.target.value })}
                    placeholder="e.g. ONERO-A7X9B2" className="h-9 font-mono"
                    data-testid={`item-certificate-${i}`} />
                </div>
              )}
              </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addItem}
              data-testid="add-item-btn">
              <Plus className="h-3 w-3 mr-1" />Add another line item
            </Button>
          </div>

          {/* Payment splits */}
          <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Payment splits</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500 font-mono-num">
                  Total: {fmtMoney(splitsTotal)}
                </span>
                {Math.abs(splitDelta) > 0.005 && (
                  <span className={`text-xs font-medium ${splitDelta < 0 ? "text-amber-700" : "text-red-700"}`}>
                    ({splitDelta > 0 ? "over" : "under"} by {fmtMoney(Math.abs(splitDelta))})
                  </span>
                )}
                {Math.abs(splitDelta) > 0.005 && (
                  <Button size="sm" variant="outline" onClick={balanceSplits}
                    data-testid="balance-splits-btn">
                    Auto-balance
                  </Button>
                )}
              </div>
            </div>
            {draft.splits.map((sp, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px_36px] items-end gap-2" data-testid={`receipt-split-row-${i}`}>
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">Method</Label>
                  <Select value={sp.method} onValueChange={(v) => updateSplit(i, { method: v })}>
                    <SelectTrigger className="h-9" data-testid={`split-method-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-2xs uppercase tracking-wide text-zinc-500">Amount</Label>
                  <Input type="number" step="0.01" value={sp.amount}
                    onChange={(e) => updateSplit(i, { amount: e.target.value })}
                    className="h-9 font-mono-num"
                    data-testid={`split-amount-${i}`} />
                </div>
                <Button variant="outline" size="icon" className="h-9 w-9"
                  disabled={draft.splits.length <= 1}
                  onClick={() => removeSplit(i)}
                  data-testid={`split-remove-${i}`}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addSplit}
              data-testid="add-split-btn">
              <Plus className="h-3 w-3 mr-1" />Add another split (e.g. HICAPS + EFTPOS)
            </Button>
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              data-testid="receipt-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="receipt-save-btn">Save receipt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Ledger tab — receipt list + search + entry point for the dialog
// ---------------------------------------------------------------------------
function SyncBadge({ status }) {
  const map = {
    matched: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "In Acuity" },
    synced_manual: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Synced" },
    no_sync_needed: { cls: "bg-zinc-100 text-zinc-600 border-zinc-200", label: "Auto" },
    missing: { cls: "bg-red-100 text-red-800 border-red-200", label: "Missing" },
    pending: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
  };
  const info = map[status] || map.pending;
  return (
    <Badge variant="outline" className={`text-2xs ${info.cls}`}
      data-testid={`sync-badge-${status}`}>
      {info.label}
    </Badge>
  );
}

function LedgerTab({ providers, catalog }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(todayISO());
  useRollingToday(date, setDate);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState("all");
  const [providerId, setProviderId] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [auditRunning, setAuditRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (date) { params.date_from = date; params.date_to = date; }
      if (q) params.q = q;
      if (method !== "all") params.method = method;
      if (providerId !== "all") params.provider_id = providerId;
      const r = await api.get("/reconciliation/receipts", { params });
      const all = r.data?.receipts || [];
      const filtered = syncFilter === "all"
        ? all
        : all.filter((row) => (row.acuity_sync_status || "pending") === syncFilter);
      setRows(filtered);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load receipts");
    } finally { setLoading(false); }
  }, [date, q, method, providerId, syncFilter]);

  useEffect(() => { load(); }, [load]);

  async function runAudit() {
    setAuditRunning(true);
    try {
      const r = await api.post("/reconciliation/audit/run");
      const d = r.data || {};
      if (d.error) {
        toast.error(d.error);
      } else if (d.missing > 0) {
        toast.warning(`Audit: ${d.matched}/${d.checked} matched · ${d.missing} still missing`);
      } else {
        toast.success(`Audit: all ${d.matched}/${d.checked} receipts matched to Acuity`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setAuditRunning(false); }
  }

  async function markSynced(id) {
    try {
      await api.post(`/reconciliation/receipts/${id}/mark-synced`);
      toast.success("Marked synced with Acuity");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark"); }
  }

  function openNew() {
    setEditing(null);
    setDlgOpen(true);
  }
  function openEdit(r) {
    setEditing(r);
    setDlgOpen(true);
  }
  async function remove(id) {
    if (!window.confirm("Delete this receipt?")) return;
    try {
      await api.delete(`/reconciliation/receipts/${id}`);
      toast.success("Receipt deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  return (
    <div className="space-y-4" data-testid="ledger-tab">
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="ledger-date-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Search client, receipt#, or product</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Smith / R-2026-0042 / Onero1" className="pl-8"
                data-testid="ledger-search-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Method (any split)</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="ledger-method-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                {PAYMENT_METHODS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={providerId} onValueChange={setProviderId}>
              <SelectTrigger data-testid="ledger-provider-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                {providers.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Acuity sync</Label>
            <Select value={syncFilter} onValueChange={setSyncFilter}>
              <SelectTrigger data-testid="ledger-sync-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="missing">Missing in Acuity</SelectItem>
                <SelectItem value="pending">Not yet audited</SelectItem>
                <SelectItem value="matched">Matched</SelectItem>
                <SelectItem value="synced_manual">Marked synced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={runAudit} disabled={auditRunning}
              data-testid="ledger-audit-btn" title="Match receipts to Acuity orders (last 14 days)">
              <RefreshCw className={`h-3 w-3 mr-1 ${auditRunning ? "animate-spin" : ""}`} />
              {auditRunning ? "Auditing…" : "Run Acuity audit"}
            </Button>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="ledger-refresh-btn">
              <RefreshCw className="h-3 w-3 mr-1" />Refresh
            </Button>
            <Button onClick={openNew} data-testid="ledger-add-btn">
              <Plus className="h-3 w-3 mr-1" />New receipt
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Date</TableHead>
              <TableHead className="w-16">Time</TableHead>
              <TableHead className="w-32">Receipt #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead className="w-24">Provider</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Splits</TableHead>
              <TableHead className="text-right w-24">Total</TableHead>
              <TableHead className="w-24">Acuity</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-6 text-sm text-zinc-500">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-6 text-sm text-zinc-500">No receipts in this window.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`ledger-row-${r.id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell>{r.time || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.receipt_number || "—"}</TableCell>
                <TableCell>{r.client_name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">
                  {r.provider_initials || (r.provider_name ? r.provider_name.split(" ").map(w => w[0]).slice(0, 2).join("") : "—")}
                </TableCell>
                <TableCell className="text-xs">
                  {(r.items || []).map((it, i) => (
                    <div key={i} className="truncate max-w-[240px]">
                      {(it.quantity || 1) > 1 && (
                        <span className="mr-1 font-mono text-zinc-500">{it.quantity}×</span>
                      )}
                      {it.description}
                      {it.certificate_code && (
                        <Badge variant="outline" className="ml-1 text-2xs bg-indigo-50 border-indigo-200 text-indigo-900 font-mono">
                          {it.certificate_code}
                        </Badge>
                      )}
                    </div>
                  ))}
                </TableCell>
                <TableCell className="space-x-1">
                  {(r.splits || []).map((sp, i) => (
                    <Badge key={i} variant="outline" className={`text-2xs ${METHOD_BADGE_CLASS[sp.method] || ""}`}>
                      {sp.method} {fmtMoney(sp.amount)}
                    </Badge>
                  ))}
                </TableCell>
                <TableCell className="text-right font-mono">{fmtMoney(r.total)}</TableCell>
                <TableCell>
                  <SyncBadge status={r.acuity_sync_status} />
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {(r.acuity_sync_status === "missing" || r.acuity_sync_status === "pending") && (
                    <Button size="sm" variant="outline"
                      onClick={() => markSynced(r.id)}
                      title="Mark this receipt as synced with Acuity (override)"
                      data-testid={`ledger-mark-synced-${r.id}`}>
                      ✓
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)}
                    data-testid={`ledger-edit-${r.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(r.id)}
                    data-testid={`ledger-delete-${r.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ReceiptDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        providers={providers}
        catalog={catalog}
        editingReceipt={editing}
        onSaved={load}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily Reconciliation tab (bucket totals + actuals form)
// ---------------------------------------------------------------------------
function DiffBadge({ diff, match }) {
  if (match) {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
      <CheckCircle2 className="h-3 w-3 mr-1" />Match
    </Badge>;
  }
  const dollars = Number(diff || 0);
  return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
    <AlertTriangle className="h-3 w-3 mr-1" />
    {dollars > 0 ? "+" : ""}{fmtMoney(dollars)}
  </Badge>;
}

function DailyTab() {
  const [date, setDate] = useState(todayISO());
  useRollingToday(date, setDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actuals, setActuals] = useState({
    hicaps: {}, nab: "", amex: "", stripe: "", cash: "", bank_transfer: "", medicare: "", other: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reconciliation/day/${date}`);
      setData(r.data);
      const a = r.data?.settlement?.actuals || {};
      setActuals({
        hicaps: Object.fromEntries(Object.entries(a.hicaps || {}).map(([k, v]) => [k, v?.toString() ?? ""])),
        nab: a.nab?.toString() ?? "",
        amex: a.amex?.toString() ?? "",
        stripe: a.stripe?.toString() ?? "",
        cash: a.cash?.toString() ?? "",
        bank_transfer: a.bank_transfer?.toString() ?? "",
        medicare: a.medicare?.toString() ?? "",
        other: a.other?.toString() ?? "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load day");
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  async function saveActuals() {
    setSaving(true);
    try {
      const body = {
        hicaps: Object.fromEntries(Object.entries(actuals.hicaps).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])),
        nab: Number(actuals.nab) || 0,
        amex: Number(actuals.amex) || 0,
        stripe: Number(actuals.stripe) || 0,
        cash: Number(actuals.cash) || 0,
        bank_transfer: Number(actuals.bank_transfer) || 0,
        medicare: Number(actuals.medicare) || 0,
        other: Number(actuals.other) || 0,
      };
      await api.put(`/reconciliation/settlements/${date}`, body);
      toast.success("Actuals saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  const b = data?.breakdown;
  const v = data?.variance;

  return (
    <div className="space-y-4" data-testid="daily-tab">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="daily-date-input" />
          </div>
          <Button variant="outline" onClick={load} disabled={loading}
            data-testid="daily-refresh-btn">
            <RefreshCw className="h-3 w-3 mr-1" />Refresh
          </Button>
          {b?.stripe_error && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {b.stripe_error}
            </span>
          )}
        </div>
      </Card>

      {!b ? (
        <Card className="p-6 text-center text-sm text-zinc-500">Loading…</Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="text-sm font-semibold mb-3">Receipts breakdown (expected)</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-medium text-zinc-700 mb-1">HICAPS by provider</div>
                  {v.hicaps.length === 0 ? (
                    <div className="text-xs text-zinc-500 pl-3">No HICAPS payments today.</div>
                  ) : v.hicaps.map((row) => (
                    <div key={row.provider_pk || "unassigned"} className="flex items-center justify-between pl-3 py-1">
                      <span className="text-zinc-700">
                        {row.provider_initials ? <span className="font-mono text-xs mr-2 text-zinc-500">{row.provider_initials}</span> : null}
                        {row.provider_name}
                      </span>
                      <span className="font-mono">{fmtMoney(row.expected)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pl-3 pt-1 mt-1 border-t border-dashed border-zinc-200 font-medium">
                    <span>Grand Total HICAPS</span>
                    <span className="font-mono">{fmtMoney(b.hicaps_total)}</span>
                  </div>
                </div>
                {[
                  ["NAB Settlement (EFTPOS / Visa / MC)", b.nab_total],
                  ["AMEX (settles separately)", b.amex_total],
                  ["Stripe (online)", b.stripe_total],
                  ["Cash", b.cash_total],
                  ["Bank transfer", b.bank_transfer_total],
                  ["Medicare", b.medicare_total],
                  ["Other", b.other_total],
                ].map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between py-1 border-t border-zinc-100">
                    <span>{label}</span>
                    <span className="font-mono">{fmtMoney(val)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 mt-2 border-t-2 border-zinc-900 text-base font-semibold">
                  <span>Grand total</span>
                  <span className="font-mono">{fmtMoney(b.grand_total)}</span>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-sm font-semibold mb-3">Actual settlements received</h3>
              <div className="space-y-2 text-sm">
                <div className="font-medium text-zinc-700">HICAPS by provider</div>
                {v.hicaps.length === 0 ? (
                  <div className="text-xs text-zinc-500 pl-3 mb-2">— no HICAPS to reconcile —</div>
                ) : v.hicaps.map((row) => (
                  <div key={row.provider_pk || "unassigned"} className="grid grid-cols-[1fr_120px_100px] items-center gap-2 py-1">
                    <span className="text-zinc-700">
                      {row.provider_initials ? <span className="font-mono text-xs mr-2 text-zinc-500">{row.provider_initials}</span> : null}
                      {row.provider_name}
                    </span>
                    <Input type="number" step="0.01"
                      value={row.provider_pk ? (actuals.hicaps[row.provider_pk] ?? "") : ""}
                      disabled={!row.provider_pk}
                      onChange={(e) => setActuals((a) => ({ ...a, hicaps: { ...a.hicaps, [row.provider_pk]: e.target.value } }))}
                      data-testid={`actual-hicaps-${row.provider_pk || "orphan"}`}
                      className="h-8" />
                    <DiffBadge diff={row.diff} match={row.match} />
                  </div>
                ))}
                {[
                  ["NAB (EFTPOS / Visa / MC)", "nab", v.nab],
                  ["AMEX", "amex", v.amex],
                  ["Stripe", "stripe", v.stripe],
                  ["Cash", "cash", v.cash],
                  ["Bank transfer", "bank_transfer", v.bank_transfer],
                  ["Medicare", "medicare", v.medicare],
                  ["Other", "other", v.other],
                ].map(([label, key, pair]) => (
                  <div key={key} className="grid grid-cols-[1fr_120px_100px] items-center gap-2 py-1 border-t border-zinc-100">
                    <span>{label}</span>
                    <Input type="number" step="0.01" value={actuals[key] ?? ""}
                      onChange={(e) => setActuals((a) => ({ ...a, [key]: e.target.value }))}
                      data-testid={`actual-${key}`} className="h-8" />
                    <DiffBadge diff={pair.diff} match={pair.match} />
                  </div>
                ))}
                <div className="flex justify-end pt-3">
                  <Button onClick={saveActuals} disabled={saving}
                    data-testid="daily-save-actuals-btn">
                    {saving ? "Saving…" : "Save actuals"}
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          <Card className={`p-4 ${v.grand.match ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-lg font-semibold">
                {v.grand.match ? (
                  <span className="text-emerald-800 flex items-center"><CheckCircle2 className="h-5 w-5 mr-2" />Reconciled ✓</span>
                ) : (
                  <span className="text-red-800 flex items-center"><AlertTriangle className="h-5 w-5 mr-2" />Difference {fmtMoney(v.grand.diff)}</span>
                )}
              </div>
              <div className="text-right text-sm">
                <div>Expected: <span className="font-mono">{fmtMoney(v.grand.expected)}</span></div>
                <div>Actual: <span className="font-mono">{fmtMoney(v.grand.actual)}</span></div>
              </div>
            </div>
            {v.alerts.length > 0 && (
              <ul className="space-y-1 text-sm" data-testid="daily-alerts">
                {v.alerts.map((a, i) => (
                  <li key={i} className={a.level === "error" ? "text-red-800" : a.level === "warning" ? "text-amber-800" : "text-zinc-700"}>
                    • {a.message}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-3 border-b border-zinc-200 flex items-center justify-between">
              <span className="text-sm font-medium">All receipts this day ({b.receipts.length})</span>
              <span className="text-xs text-zinc-500">
                Stripe rows are auto-pulled from the Stripe balance;
                manual receipts can be edited in the Receipts journal tab.
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Time</TableHead>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="w-20">Provider</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Splits</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {b.receipts.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.time || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.receipt_number || "—"}</TableCell>
                    <TableCell>{r.client_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.provider_initials || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {(r.items || []).map((it, i) => (
                        <div key={i}>
                          {(it.quantity || 1) > 1 && (
                            <span className="mr-1 font-mono text-zinc-500">{it.quantity}×</span>
                          )}
                          {it.description}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell className="space-x-1">
                      {(r.splits || []).map((sp, i) => (
                        <Badge key={i} variant="outline" className={`text-2xs ${METHOD_BADGE_CLASS[sp.method] || ""}`}>
                          {sp.method} {fmtMoney(sp.amount)}
                        </Badge>
                      ))}
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reports tab (day / week / month presets or custom range)
// ---------------------------------------------------------------------------
function ReportsTab() {
  const [preset, setPreset] = useState("week");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return melDate(d);
  });
  const [dateTo, setDateTo] = useState(todayISO());
  useRollingToday(dateTo, setDateTo);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  function applyPreset(p) {
    setPreset(p);
    const to = new Date();
    const from = new Date();
    if (p === "day") from.setDate(to.getDate());
    else if (p === "week") from.setDate(to.getDate() - 6);
    else if (p === "month") from.setDate(to.getDate() - 29);
    setDateFrom(melDate(from));
    setDateTo(melDate(to));
  }

  async function run() {
    setLoading(true);
    try {
      const r = await api.get("/reconciliation/report", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally { setLoading(false); }
  }

  const b = data?.breakdown;
  const v = data?.variance;

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1">
            {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([k, l]) => (
              <Button key={k} size="sm" variant={preset === k ? "default" : "outline"}
                onClick={() => applyPreset(k)} data-testid={`report-preset-${k}`}>{l}</Button>
            ))}
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
              data-testid="report-from-input" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
              data-testid="report-to-input" />
          </div>
          <Button onClick={run} disabled={loading} data-testid="report-run-btn">Run report</Button>
        </div>
      </Card>

      {!data ? (
        <Card className="p-6 text-center text-sm text-zinc-500">Choose a range and click Run report.</Card>
      ) : (
        <>
          <Card className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-zinc-500">Receipts total</div>
                <div className="text-lg font-semibold font-mono">{fmtMoney(b.grand_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">HICAPS grand</div>
                <div className="text-lg font-semibold font-mono">{fmtMoney(b.hicaps_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">NAB (EFTPOS/Visa/MC)</div>
                <div className="text-lg font-semibold font-mono">{fmtMoney(b.nab_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">AMEX</div>
                <div className="text-lg font-semibold font-mono">{fmtMoney(b.amex_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Stripe</div>
                <div className="text-lg font-semibold font-mono">{fmtMoney(b.stripe_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Cash</div>
                <div className="font-mono">{fmtMoney(b.cash_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Bank transfer</div>
                <div className="font-mono">{fmtMoney(b.bank_transfer_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Medicare</div>
                <div className="font-mono">{fmtMoney(b.medicare_total)}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Other</div>
                <div className="font-mono">{fmtMoney(b.other_total)}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-zinc-500">
              {data.days_reconciled} of {data.total_days} days have typed-in actuals.
              {" "}Overall reconciliation: {v.grand.match ? (
                <span className="text-emerald-700 font-semibold">✓ balanced</span>
              ) : (
                <span className="text-red-700 font-semibold">off by {fmtMoney(v.grand.diff)}</span>
              )}
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-3 border-b border-zinc-200 text-sm font-medium">HICAPS breakdown by provider</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Diff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {v.hicaps.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm py-4 text-zinc-500">No HICAPS payments in this range.</TableCell></TableRow>
                ) : v.hicaps.map((row) => (
                  <TableRow key={row.provider_pk || "unassigned"}>
                    <TableCell>
                      {row.provider_initials ? <span className="font-mono text-xs mr-2 text-zinc-500">{row.provider_initials}</span> : null}
                      {row.provider_name}
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(row.expected)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(row.actual)}</TableCell>
                    <TableCell className="text-right"><DiffBadge diff={row.diff} match={row.match} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function Reconciliation() {
  const { user } = useAuth();
  // Staff role sees only the New receipt + Receipts Journal tabs (they
  // add / edit receipts at the front desk); Daily reconciliation,
  // Providers and Reports remain owner/manager-only.
  const isStaffOnly = user?.role === "staff";
  const [searchParams] = useSearchParams();
  // Deep-link support: /reconciliation?tab=new lands directly on the New
  // receipt tab (used by the Receipt buttons on Appointments / Orders).
  // Also treat receipt-context params as an implicit "go to new" hint so
  // legacy links like /receipts?source=appointment&id=123 still work.
  const initialTab = (() => {
    const explicit = searchParams.get("tab");
    if (explicit) return explicit;
    if (searchParams.get("regen")
        || searchParams.get("source")
        || searchParams.get("id")) return "new";
    return "ledger";
  })();
  const [tab, setTab] = useState(initialTab);
  const [providers, setProviders] = useState([]);
  const [catalog, setCatalog] = useState([]);

  const loadProviders = useCallback(async () => {
    try {
      const r = await api.get("/reconciliation/providers", { params: { include_inactive: true } });
      setProviders(r.data?.providers || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load providers"); }
  }, []);

  const loadCatalog = useCallback(async () => {
    try {
      const r = await api.get("/reconciliation/acuity-catalog");
      setCatalog(r.data?.items || []);
    } catch (e) {
      // Non-fatal — catalog is optional. Manual descriptions still work.
      setCatalog([]);
    }
  }, []);

  useEffect(() => { loadProviders(); loadCatalog(); }, [loadProviders, loadCatalog]);

  const activeProviders = useMemo(() => providers.filter((p) => p.active), [providers]);

  const tabs = isStaffOnly
    ? [
        ["new", "New receipt"],
        ["ledger", "Receipts journal"],
      ]
    : [
        ["new", "New receipt"],
        ["daily", "Daily reconciliation"],
        ["ledger", "Receipts journal"],
        ["providers", "Providers"],
        ["reports", "Reports"],
      ];

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      <div>
        <div className="text-xs uppercase tracking-wide text-zinc-500">Payments</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900">
          Receipts
        </h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
          Issue new receipts, keep the receipts journal tidy, and reconcile each day&apos;s
          takings against HICAPS (per provider), NAB EFTPOS, Stripe, Cash, Medicare
          and Bank transfer settlements.
        </p>
      </div>

      <div className="flex gap-1 border-b border-zinc-200">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`recon-tab-${k}`}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors " +
              (tab === k
                ? "border-zinc-900 text-zinc-900"
                : "border-transparent text-zinc-500 hover:text-zinc-800")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "new" && <Receipts />}
      {tab === "daily" && <DailyTab />}
      {tab === "ledger" && <LedgerTab providers={activeProviders} catalog={catalog} />}
      {tab === "providers" && <ProvidersTab providers={providers} refresh={loadProviders} />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}
